'use strict';

const DEFAULT_CONFIRM_DELAY_MS = 3000;
const PAGEBREAK_SELECTOR = '[role="doc-pagebreak"]';

/**
 * Detects epub:type="pagebreak" elements as they become visible in an
 * EPUB iframe and fires a callback after the element has remained
 * visible for a configurable delay.
 *
 * Uses manual getBoundingClientRect() checks instead of IntersectionObserver
 * because Readium's paginated mode uses CSS columns + transforms that do
 * not trigger IntersectionObserver callbacks on page changes.
 */
export class PagebreakTracker {
    /**
     * @param {Function} onPagebreakConfirmed - Called with pagebreak info
     *   after the element has been visible for the configured delay.
     *   Receives: { title: string, id: string, role: string }
     * @param {Object} [options={}]
     * @param {number} [options.confirmDelayMs] - Milliseconds the element
     *   must remain visible before confirming. Defaults to 3000.
     */
    constructor(onPagebreakConfirmed, { confirmDelayMs } = {}) {
        this._onPagebreakConfirmed = onPagebreakConfirmed;
        this._confirmDelayMs = confirmDelayMs ?? DEFAULT_CONFIRM_DELAY_MS;
        /** @type {Map<Element, number>} */
        this._timers = new Map();
        /** @type {{ frameWindow: Window, pagebreaks: NodeList }[]} */
        this._frames = [];
        /** @type {Set<number>} */
        this._retryIntervals = new Set();
    }

    /**
     * Registers an iframe for pagebreak tracking.
     * Guards against placeholder bodies and retries until real content is ready.
     *
     * @param {Window} frameWindow
     */
    observeFrame(frameWindow) {
        const doc = frameWindow.document;
        if (!doc?.body) return;

        if (doc.body.textContent?.length <= 50) {
            let attempts = 0;
            const retryId = setInterval(() => {
                attempts++;
                if (attempts > 20) {
                    clearInterval(retryId);
                    this._retryIntervals.delete(retryId);
                    return;
                }
                try {
                    if (doc.body?.textContent?.length > 50) {
                        clearInterval(retryId);
                        this._retryIntervals.delete(retryId);
                        this._registerFrame(frameWindow);
                    }
                } catch (_e) {
                    clearInterval(retryId);
                    this._retryIntervals.delete(retryId);
                }
            }, 500);
            this._retryIntervals.add(retryId);
            return;
        }

        this._registerFrame(frameWindow);
    }

    /** @private */
    _registerFrame(frameWindow) {
        const doc = frameWindow.document;

        this._frames = this._frames.filter(f => f.frameWindow.document !== doc);

        const pagebreaks = doc.querySelectorAll(PAGEBREAK_SELECTOR);
        if (pagebreaks.length === 0) return;

        this._frames.push({ frameWindow, pagebreaks });
        this.checkVisibility();
    }

    /**
     * Checks which pagebreaks are currently visible in the viewport
     * and manages timers accordingly. Call on every position/page change.
     */
    checkVisibility() {
        // Prune stale frames (destroyed/recycled iframes)
        this._frames = this._frames.filter(({ frameWindow }) => {
            try {
                return frameWindow.document?.body !== null;
            } catch (_e) {
                return false;
            }
        });

        const nowVisible = new Set();

        for (const { frameWindow, pagebreaks } of this._frames) {
            const viewportWidth = frameWindow.innerWidth;
            const viewportHeight = frameWindow.innerHeight;

            for (const el of pagebreaks) {
                const rect = el.getBoundingClientRect();
                const isVisible =
                    rect.right > 0 && rect.left < viewportWidth && rect.bottom >= 0 && rect.top <= viewportHeight;

                if (isVisible) {
                    nowVisible.add(el);
                }
            }
        }

        for (const el of nowVisible) {
            if (!this._timers.has(el)) {
                const timerId = setTimeout(() => {
                    this._timers.delete(el);
                    this._confirmPagebreak(el);
                }, this._confirmDelayMs);
                this._timers.set(el, timerId);
            }
        }

        for (const [el, timerId] of this._timers) {
            if (!nowVisible.has(el)) {
                clearTimeout(timerId);
                this._timers.delete(el);
            }
        }
    }

    /** @private */
    _confirmPagebreak(element) {
        if (!this._onPagebreakConfirmed) return;
        this._onPagebreakConfirmed({
            title: element.getAttribute('title'),
            id: element.id,
        });
    }

    disconnectAll() {
        this._retryIntervals.forEach(id => clearInterval(id));
        this._retryIntervals.clear();
        this._timers.forEach(timerId => clearTimeout(timerId));
        this._timers.clear();
        this._frames = [];
    }

    destroy() {
        this.disconnectAll();
        this._onPagebreakConfirmed = null;
    }
}
